import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, XCircle, Clock } from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function AdminPayments() {
  const { user, loading: authLoading } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? "/payments/manual" : `/payments/manual?status=${filter}`;
      const { data } = await api.get(url);
      setPayments(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  if (authLoading) return <p className="text-muted-foreground">Loading...</p>;
  if (!user || user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <p className="text-muted-foreground">Admin only.</p>;

  const act = async (p, action) => {
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} ${p.user_email}?`)) return;
    try {
      await api.post(`/payments/manual/${p.id}/${action}`);
      toast.success(action === "approve" ? "Approved — user upgraded to Premium" : "Rejected");
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const badge = (s) =>
    s === "approved" ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-primary text-primary-foreground rounded-sm">
        <CheckCircle2 className="w-3 h-3" /> Approved
      </span>
    ) : s === "rejected" ? (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-muted text-muted-foreground rounded-sm">
        <XCircle className="w-3 h-3" /> Rejected
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 bg-secondary text-secondary-foreground rounded-sm">
        <Clock className="w-3 h-3" /> Pending
      </span>
    );

  return (
    <div>
      <div className="flex items-baseline justify-between border-b-2 border-primary pb-4 mb-8">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-primary mb-2 hover:underline">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
          <div className="font-ui text-xs uppercase tracking-[0.3em] text-muted-foreground">Manual Payments</div>
          <h1 className="font-kannada-heading text-3xl mt-1">UPI / PhonePe ಪಾವತಿಗಳು</h1>
        </div>
        <div className="flex gap-2">
          {["pending", "approved", "rejected", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 border ${filter === f ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"} rounded-sm uppercase tracking-widest font-semibold`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Transaction ID</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-40 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No payments in this bucket.</TableCell></TableRow>
            ) : (
              payments.map((p) => (
                <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                  <TableCell>
                    <div className="text-sm font-semibold">{p.user_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{p.user_email}</div>
                    {p.payer_phone && <div className="text-xs text-muted-foreground">📞 {p.payer_phone}</div>}
                  </TableCell>
                  <TableCell className="uppercase text-xs font-bold">{p.method}</TableCell>
                  <TableCell className="font-mono">₹{p.amount}</TableCell>
                  <TableCell className="font-mono text-xs">{p.transaction_id}</TableCell>
                  <TableCell>{badge(p.status)}</TableCell>
                  <TableCell className="text-right">
                    {p.status === "pending" && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" onClick={() => act(p, "approve")} className="rounded-none" data-testid={`approve-${p.id}`}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => act(p, "reject")} className="rounded-none" data-testid={`reject-${p.id}`}>
                          Reject
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
